import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Select, Spin } from "antd";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import {
  useCustomMutation,
  useInvalidate,
  useOne,
  useTranslate,
} from "@refinedev/core";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import { useSession } from "hooks/useSession";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import type {
  CallToAction,
  MediaAsset,
  MediaProjectInstanceResponse,
} from "../../../types";
import { normalizeActionUrl, hasLabelAndValue } from "../../../utils";
import { DashedButton } from "@libertify/ui";
import {
  DSection,
  DSectionLabel,
  LinkItemRow,
  LinkItemLabel,
  AddLinkForm,
  AddLinkFormRow,
  AddLinkFormActions,
  ConfirmLinkBtn,
  CancelLinkBtn,
  TrashBtn,
} from "./drawer-styled";

type LinksCtaSectionProps = {
  projectId: string;
  instanceId: string;
  ctaGroupId?: string | null;
  onCtaChange?: () => void;
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  ExternalLink: "🌐",
  CalendarAppointment: "📅",
  DocumentDownload: "📄",
  Email: "✉️",
  Phone: "📞",
};

const getCtaLabel = (cta: CallToAction): string => {
  const labels = cta.ui_definition.labels;
  // Pick the first available label
  return Object.values(labels)[0] || cta.action.url || cta.id;
};

export const LinksCtaSection: React.FC<LinksCtaSectionProps> = ({
  projectId,
  instanceId,
  ctaGroupId,
  onCtaChange,
}) => {
  const t = useTranslate();
  const { currentOrganization: organization } = useSession();
  const { mutateAsync } = useCustomMutation();
  const invalidate = useInvalidate();
  // BE marks the publication OutOfDate on CTA asset PATCH, but not on
  // creation, so we flag it ourselves only after creating a new CTA group
  // (see handleAddLink). No-op when already OutOfDate or nothing is live yet.
  const { markAsOutOfDate } = useProjectInstance({ projectId, instanceId });
  const [addingLink, setAddingLink] = useState(false);
  const [linkType, setLinkType] = useState("ExternalLink");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [ctaActions, setCtaActions] = useState<CallToAction[]>([]);
  const [saving, setSaving] = useState(false);
  // Tracks the group ID after first creation so subsequent adds target the same group
  const [createdGroupId, setCreatedGroupId] = useState<string | null>(null);

  // Always fetch instance to discover the CTA asset the player actually uses
  const { data: instanceData, isFetching: isFetchingInstance } =
    useOne<MediaProjectInstanceResponse>({
      resource: `media/projects/${projectId}/instances`,
      id: instanceId,
      queryOptions: {
        enabled: Boolean(projectId) && Boolean(instanceId),
        staleTime: Infinity,
      },
    });

  // Find the CTA asset from instance assets (same discovery as the player)
  const instanceCtaAsset = useMemo(() => {
    if (!instanceData?.data.assets) return undefined;
    const assets = instanceData.data.assets as Record<string, MediaAsset>;
    return Object.values(assets).find((a) => a.asset_type === "CallToActions");
  }, [instanceData?.data.assets]);

  // Instance asset is the source of truth (what the player uses).
  // Fall back to ctaGroupId prop, then locally created ID.
  const effectiveGroupId =
    instanceCtaAsset?.id ?? createdGroupId ?? ctaGroupId ?? null;

  // Use the path directly from the instance asset when available
  const ctaAssetPath = instanceCtaAsset?.path ?? null;

  const { saveCallToActionsAsset } = useMediaAssetsStorage({
    organizationId: organization?.id,
  });

  // Fetch the JSON content from the asset path
  useEffect(() => {
    (async () => {
      if (!ctaAssetPath) return;
      try {
        const response = await fetch(ctaAssetPath);
        const json = await response.json();
        setCtaActions(json.actions ?? []);
      } catch {
        setCtaActions([]);
      }
    })();
  }, [ctaAssetPath]);

  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      invalidate({
        resource: `media/projects/${projectId}/instances`,
        id: instanceId,
        invalidates: ["detail"],
      }),
      invalidate({
        resource: "media/projects",
        id: projectId,
        invalidates: ["detail"],
      }),
    ]);
  }, [invalidate, projectId, instanceId]);

  const saveActions = useCallback(
    async (actions: CallToAction[]) => {
      if (!effectiveGroupId) return;
      setSaving(true);
      try {
        await saveCallToActionsAsset({ actions }, effectiveGroupId);
        await invalidateQueries();
        // No markAsOutOfDate here: this is a PATCH of an existing CTA asset,
        // and BE already marks the publication out of date for that.
        onCtaChange?.();
      } finally {
        setSaving(false);
      }
    },
    [effectiveGroupId, saveCallToActionsAsset, invalidateQueries, onCtaChange]
  );

  const isDocumentDownload = linkType === "DocumentDownload";
  const canSubmitLink =
    (isDocumentDownload && linkLabel.trim().length > 0) ||
    hasLabelAndValue(linkLabel, linkUrl);

  const handleAddLink = async () => {
    if (!canSubmitLink) return;

    const action: CallToAction["action"] = { type: linkType };
    if (!isDocumentDownload) {
      action.url = normalizeActionUrl(linkType, linkUrl);
    }

    const newCta: CallToAction = {
      id: crypto.randomUUID(),
      ui_definition: {
        type: "Button",
        labels: { en: linkLabel.trim() },
      },
      action,
    };

    // Update UI immediately, save in the background
    const updatedActions = [...ctaActions, newCta];
    setCtaActions(updatedActions);
    setAddingLink(false);
    setLinkType("ExternalLink");
    setLinkLabel("");
    setLinkUrl("");

    if (effectiveGroupId) {
      await saveActions(updatedActions);
    } else {
      setSaving(true);
      try {
        const assetId: string | undefined = await saveCallToActionsAsset(
          { actions: [newCta] },
          undefined,
          { category: "MediaMeta", description: "Experience CTAs" }
        );
        if (assetId) {
          setCreatedGroupId(assetId);
          await mutateAsync({
            url: `media/projects/${projectId}/instances/${instanceId}/settings`,
            method: "patch",
            values: { cta_group_id: assetId },
            successNotification: false,
            errorNotification: false,
          });
          await invalidateQueries();
          await markAsOutOfDate().catch(() => undefined);
          onCtaChange?.();
        }
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteCta = async (ctaId: string) => {
    const updatedActions = ctaActions.filter((cta) => cta.id !== ctaId);
    setCtaActions(updatedActions);
    await saveActions(updatedActions);
  };

  const handleCancel = () => {
    setAddingLink(false);
    setLinkType("ExternalLink");
    setLinkLabel("");
    setLinkUrl("");
  };

  // Only show spinner on initial load, not during background refetches
  const isLoading =
    isFetchingInstance && ctaActions.length === 0 && !ctaAssetPath;

  const inputPlaceholder = useMemo(() => {
    if (linkType === "Email") return "name@example.com";
    if (linkType === "Phone") return "+33...";
    return "https://...";
  }, [linkType]);

  return (
    <DSection>
      <DSectionLabel>
        {t("media.components.ProjectSettingsDrawer.linksAndCtas")}
        {saving && <Spin size="small" style={{ marginLeft: 8 }} />}
      </DSectionLabel>
      <p style={{ fontSize: 12, color: "#9CA3AF", margin: "0 0 12px" }}>
        {t("media.components.ProjectSettingsDrawer.linksDescription")}
      </p>

      {isLoading ? (
        <Spin size="small" />
      ) : (
        ctaActions.map((cta) => (
          <LinkItemRow key={cta.id}>
            <span style={{ flexShrink: 0 }}>
              {ACTION_TYPE_LABELS[cta.action.type] ?? "🔗"}
            </span>
            <LinkItemLabel>{getCtaLabel(cta)}</LinkItemLabel>
            <TrashBtn onClick={() => void handleDeleteCta(cta.id)}>
              <TrashIcon size={14} color="#EF4444" />
            </TrashBtn>
          </LinkItemRow>
        ))
      )}

      {addingLink ? (
        <AddLinkForm>
          <AddLinkFormRow>
            <Select
              value={linkType}
              onChange={setLinkType}
              style={{ width: 130 }}
              size="small"
              options={[
                { value: "CalendarAppointment", label: "📅 Calendar" },
                { value: "ExternalLink", label: "🌐 Website" },
                { value: "Phone", label: "📞 Call" },
                {
                  value: "DocumentDownload",
                  label: "📄 Document",
                  disabled: ctaActions.some(
                    (c) => c.action.type === "DocumentDownload"
                  ),
                },
                { value: "Email", label: "✉️ Email" },
              ]}
            />
            <Input
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder={t(
                "media.components.ProjectSettingsDrawer.linkLabelPlaceholder"
              )}
              size="small"
              style={{ flex: 1 }}
            />
          </AddLinkFormRow>
          {linkType === "DocumentDownload" ? (
            <p style={{ fontSize: 12, color: "#9CA3AF", margin: "4px 0" }}>
              {t("media.components.ProjectSettingsDrawer.documentDownloadHint")}
            </p>
          ) : (
            <AddLinkFormRow>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder={inputPlaceholder}
                size="small"
                style={{ flex: 1 }}
              />
            </AddLinkFormRow>
          )}
          <AddLinkFormActions>
            <CancelLinkBtn onClick={handleCancel}>
              {t("buttons.cancel")}
            </CancelLinkBtn>
            <ConfirmLinkBtn
              onClick={() => void handleAddLink()}
              disabled={!canSubmitLink}
            >
              {t("buttons.add", "Add")}
            </ConfirmLinkBtn>
          </AddLinkFormActions>
        </AddLinkForm>
      ) : (
        <DashedButton onClick={() => setAddingLink(true)}>
          <PlusIcon size={13} />{" "}
          {t("media.components.ProjectSettingsDrawer.addLink")}
        </DashedButton>
      )}
    </DSection>
  );
};
